"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const DISMISS_KEY_PREFIX = "railyard_app_update_dismissed:";

type BuildMeta = {
  buildId: string;
  builtAt?: string;
};

function dismissKey(offeredBuildId: string): string {
  return `${DISMISS_KEY_PREFIX}${offeredBuildId}`;
}

function isDismissed(offeredBuildId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(dismissKey(offeredBuildId)) === "1";
  } catch {
    return false;
  }
}

/**
 * Polls `/build-meta.json` for a newer deploy than the bundle's
 * `NEXT_PUBLIC_APP_BUILD_ID`. Skips entirely in `next dev`.
 */
export function useAppUpdate() {
  const currentBuildId = process.env.NEXT_PUBLIC_APP_BUILD_ID;
  const [offeredBuildId, setOfferedBuildId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const latestCheckRef = useRef(0);
  const dismissedBuildIdsRef = useRef(new Set<string>());

  const checkForUpdate = useCallback(async () => {
    if (process.env.NODE_ENV === "development") return;
    if (!currentBuildId) return;

    const checkId = ++latestCheckRef.current;

    try {
      const res = await fetch(`/build-meta.json?_=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return;

      const meta = (await res.json()) as BuildMeta;
      if (checkId !== latestCheckRef.current) return;

      if (!meta.buildId || meta.buildId === currentBuildId) {
        setOfferedBuildId(null);
        setVisible(false);
        return;
      }

      const dismissed =
        dismissedBuildIdsRef.current.has(meta.buildId) ||
        isDismissed(meta.buildId);
      setOfferedBuildId(meta.buildId);
      setVisible(!dismissed);
    } catch {
      // Transient network failure — retry on the next poll or tab focus.
    }
  }, [currentBuildId]);

  const dismiss = useCallback(() => {
    if (!offeredBuildId) return;
    // Keep dismissal stable for this page session even if sessionStorage is
    // disabled or temporarily unavailable.
    dismissedBuildIdsRef.current.add(offeredBuildId);
    try {
      window.sessionStorage.setItem(dismissKey(offeredBuildId), "1");
    } catch {
      // sessionStorage unavailable — hide for this page load only.
    }
    setVisible(false);
  }, [offeredBuildId]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") return;

    const initialCheck = window.setTimeout(() => {
      void checkForUpdate();
    }, 0);

    const interval = window.setInterval(() => {
      void checkForUpdate();
    }, POLL_INTERVAL_MS);

    const recheckIfVisible = () => {
      if (document.visibilityState === "visible") {
        void checkForUpdate();
      }
    };
    window.addEventListener("focus", recheckIfVisible);
    document.addEventListener("visibilitychange", recheckIfVisible);

    return () => {
      window.clearTimeout(initialCheck);
      window.clearInterval(interval);
      window.removeEventListener("focus", recheckIfVisible);
      document.removeEventListener("visibilitychange", recheckIfVisible);
    };
  }, [checkForUpdate]);

  return {
    updateAvailable: visible && offeredBuildId !== null,
    dismiss,
    reload,
  };
}
